import {
  useCustomer,
  useDeleteCustomer,
  useHomeownersWithHubsListView,
} from "common";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import {
  Button,
  Card,
  Separator,
  Spinner,
  XStack,
  YStack,
  Text,
  Paragraph,
} from "tamagui";

import { CustomerInfoCard } from "./components/CustomerInfoCard";
import ConfirmDialog from "../../components/ConfirmDialog";

export const CustomerView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { customer, error, loading } = useCustomer(id as string);
  const { remove } = useDeleteCustomer();
  // Hub ↔ customer lives only in UserHubRelation; this query is the one place
  // that join is already done server-side.
  // ponytail: listHomeownersWithHubs scans the fleet; narrow via
  // getUserHubRelationsByCustomer if hubs > ~250
  const { homeownerOverviews, loading: hubsLoading } =
    useHomeownersWithHubsListView();
  const hubs =
    homeownerOverviews.find((entry) => entry.customer?.id === id)?.hubs ?? [];

  const hubStatusLabel = (status?: string) =>
    t(`baseStations.table.statusValue.${(status ?? "").toLowerCase()}`, {
      defaultValue: t("baseStations.table.statusValue.unknown"),
    });

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleCustomerDeleteSubmit = async () => {
    try {
      if (id) {
        await remove(id);
        navigate("/customers");
      }
    } catch (error) {
      console.error("Error while deleting customer", error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
        <Spinner size="large" />
        <Text marginTop="$2">{t("common.loading", "Loading...")}</Text>
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack padding="$4">
        <Text color="$red10" fontSize="$5">
          {t("common.error", "Error loading customers.")}
        </Text>
        {error && error.message && <Paragraph>{error.message}</Paragraph>}
      </YStack>
    );
  }

  return (
    <YStack
      flex={1}
      padding="$4"
      space="$4"
      backgroundColor="$backgroundSoft"
      $xs={{ paddingHorizontal: 0 }}
    >
      <XStack
        justifyContent="space-between"
        alignItems="center"
        marginBottom="$2"
        flexWrap="wrap"
        gap="$3"
      >
        <Text fontSize="$9" fontWeight="bold" color="$color">
          {t("customerView.title", "Customer View")}
        </Text>
        <XStack justifyContent="space-between" alignItems="center" space="$4">
          <Button
            color="red"
            size="$4"
            onPress={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
          <Button
            theme="active"
            size="$4"
            onPress={() => navigate("/customers")}
          >
            Go Back
          </Button>
        </XStack>
      </XStack>
      <YStack space="$4" flex={1} paddingTop="$4">
        {customer && <CustomerInfoCard customer={customer} />}
        <Card
          elevate
          bordered
          padding="$5"
          borderRadius="$4"
          space
          $xs={{ padding: "$4" }}
        >
          <YStack space="$3">
            <Text fontSize="$7" fontWeight="700">
              {t("customerView.baseStations.title")}
            </Text>
            <Separator borderColor="$gray5" />
            {hubsLoading ? (
              <Spinner alignSelf="flex-start" />
            ) : hubs.length === 0 ? (
              <Text color="$gray10" fontSize="$4">
                {t("customerView.baseStations.empty")}
              </Text>
            ) : (
              hubs.map((hub: any) => (
                <XStack
                  key={hub.id}
                  justifyContent="space-between"
                  alignItems="center"
                  gap="$3"
                >
                  <YStack space="$1" flexShrink={1}>
                    <Text fontSize="$4">
                      {hub.name || t("customerView.baseStations.unnamed")}
                    </Text>
                    <Text fontSize="$2" color="$gray10">
                      {hub.serialNumber}
                    </Text>
                  </YStack>
                  <Text fontSize="$2" color="$gray10">
                    {hubStatusLabel(hub.status)}
                  </Text>
                </XStack>
              ))
            )}
          </YStack>
        </Card>
      </YStack>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => handleCustomerDeleteSubmit()}
        title={t("customerView.delete.confirm", "Delete Customer?")}
        description={t(
          "customerView.delete.description",
          "Are you sure you want to delete this Customer? This action cannot be undone.",
        )}
      ></ConfirmDialog>
    </YStack>
  );
};

export default CustomerView;
